/**
 * Códigos de país (DDI) usados nos campos de telefone da plataforma.
 *
 * O valor persistido é o próprio DDI ("+55"), por isso a lista é desduplicada:
 * países que dividem o mesmo código aparecem em uma única opção com os nomes
 * juntos. Assim o valor continua único no dropdown e o que volta do banco
 * sempre encontra a opção correspondente.
 */
export interface PaisTelefone {
  /** Código internacional com "+", exatamente como é gravado no banco. */
  ddi: string;
  /** Nome exibido no dropdown. */
  nome: string;
  /** Bandeira em emoji: evita dependência externa de ícones. */
  bandeira: string;
}

/** DDI assumido em cadastros antigos e em campos ainda sem país escolhido. */
export const DDI_PADRAO = "+55";

export const PAISES_TELEFONE: PaisTelefone[] = [
  { ddi: "+55", nome: "Brasil", bandeira: "🇧🇷" },
  { ddi: "+1", nome: "Estados Unidos / Canadá", bandeira: "🇺🇸" },
  { ddi: "+244", nome: "Angola", bandeira: "🇦🇴" },
  { ddi: "+54", nome: "Argentina", bandeira: "🇦🇷" },
  { ddi: "+61", nome: "Austrália", bandeira: "🇦🇺" },
  { ddi: "+43", nome: "Áustria", bandeira: "🇦🇹" },
  { ddi: "+32", nome: "Bélgica", bandeira: "🇧🇪" },
  { ddi: "+591", nome: "Bolívia", bandeira: "🇧🇴" },
  { ddi: "+56", nome: "Chile", bandeira: "🇨🇱" },
  { ddi: "+86", nome: "China", bandeira: "🇨🇳" },
  { ddi: "+57", nome: "Colômbia", bandeira: "🇨🇴" },
  { ddi: "+506", nome: "Costa Rica", bandeira: "🇨🇷" },
  { ddi: "+53", nome: "Cuba", bandeira: "🇨🇺" },
  { ddi: "+45", nome: "Dinamarca", bandeira: "🇩🇰" },
  { ddi: "+593", nome: "Equador", bandeira: "🇪🇨" },
  { ddi: "+503", nome: "El Salvador", bandeira: "🇸🇻" },
  { ddi: "+34", nome: "Espanha", bandeira: "🇪🇸" },
  { ddi: "+351", nome: "Portugal", bandeira: "🇵🇹" },
  { ddi: "+33", nome: "França", bandeira: "🇫🇷" },
  { ddi: "+49", nome: "Alemanha", bandeira: "🇩🇪" },
  { ddi: "+30", nome: "Grécia", bandeira: "🇬🇷" },
  { ddi: "+502", nome: "Guatemala", bandeira: "🇬🇹" },
  { ddi: "+592", nome: "Guiana", bandeira: "🇬🇾" },
  { ddi: "+509", nome: "Haiti", bandeira: "🇭🇹" },
  { ddi: "+504", nome: "Honduras", bandeira: "🇭🇳" },
  { ddi: "+91", nome: "Índia", bandeira: "🇮🇳" },
  { ddi: "+62", nome: "Indonésia", bandeira: "🇮🇩" },
  { ddi: "+353", nome: "Irlanda", bandeira: "🇮🇪" },
  { ddi: "+972", nome: "Israel", bandeira: "🇮🇱" },
  { ddi: "+39", nome: "Itália", bandeira: "🇮🇹" },
  { ddi: "+81", nome: "Japão", bandeira: "🇯🇵" },
  { ddi: "+52", nome: "México", bandeira: "🇲🇽" },
  { ddi: "+258", nome: "Moçambique", bandeira: "🇲🇿" },
  { ddi: "+264", nome: "Namíbia", bandeira: "🇳🇦" },
  { ddi: "+505", nome: "Nicarágua", bandeira: "🇳🇮" },
  { ddi: "+234", nome: "Nigéria", bandeira: "🇳🇬" },
  { ddi: "+47", nome: "Noruega", bandeira: "🇳🇴" },
  { ddi: "+64", nome: "Nova Zelândia", bandeira: "🇳🇿" },
  { ddi: "+31", nome: "Países Baixos", bandeira: "🇳🇱" },
  { ddi: "+507", nome: "Panamá", bandeira: "🇵🇦" },
  { ddi: "+595", nome: "Paraguai", bandeira: "🇵🇾" },
  { ddi: "+51", nome: "Peru", bandeira: "🇵🇪" },
  { ddi: "+48", nome: "Polônia", bandeira: "🇵🇱" },
  { ddi: "+44", nome: "Reino Unido", bandeira: "🇬🇧" },
  { ddi: "+420", nome: "República Tcheca", bandeira: "🇨🇿" },
  { ddi: "+1809", nome: "República Dominicana", bandeira: "🇩🇴" },
  { ddi: "+40", nome: "Romênia", bandeira: "🇷🇴" },
  { ddi: "+7", nome: "Rússia / Cazaquistão", bandeira: "🇷🇺" },
  { ddi: "+27", nome: "África do Sul", bandeira: "🇿🇦" },
  { ddi: "+82", nome: "Coreia do Sul", bandeira: "🇰🇷" },
  { ddi: "+46", nome: "Suécia", bandeira: "🇸🇪" },
  { ddi: "+41", nome: "Suíça", bandeira: "🇨🇭" },
  { ddi: "+66", nome: "Tailândia", bandeira: "🇹🇭" },
  { ddi: "+90", nome: "Turquia", bandeira: "🇹🇷" },
  { ddi: "+598", nome: "Uruguai", bandeira: "🇺🇾" },
  { ddi: "+58", nome: "Venezuela", bandeira: "🇻🇪" },
  { ddi: "+84", nome: "Vietnã", bandeira: "🇻🇳" },
  { ddi: "+971", nome: "Emirados Árabes Unidos", bandeira: "🇦🇪" },
];

/** Lista pronta para o dropdown, sem DDI repetido e com o Brasil no topo. */
export const OPCOES_PAISES_TELEFONE: PaisTelefone[] = PAISES_TELEFONE
  .filter((pais, indice) => PAISES_TELEFONE.findIndex((item) => item.ddi === pais.ddi) === indice);

const PAISES_POR_DDI = new Map<string, PaisTelefone>(
  OPCOES_PAISES_TELEFONE.map((pais) => [pais.ddi, pais])
);

/**
 * Aceita "55", "+55" ou vazio e devolve sempre "+55". Cadastros antigos não têm
 * o campo preenchido: o padrão evita telefone sem país na tela e no envio.
 */
export function normalizarDdi(valor: unknown): string {
  const digitos = String(valor ?? "").replace(/\D/g, "").slice(0, 4);
  if (!digitos) return DDI_PADRAO;
  const ddi = `+${digitos}`;
  return PAISES_POR_DDI.has(ddi) ? ddi : DDI_PADRAO;
}

export function paisPorDdi(valor: unknown): PaisTelefone {
  const ddi = normalizarDdi(valor);
  return PAISES_POR_DDI.get(ddi) || PAISES_POR_DDI.get(DDI_PADRAO) as PaisTelefone;
}

/**
 * Número completo em E.164 ("+5583999999999") para WhatsApp, SMS e links tel:.
 * O "+" é o que diz ao servidor que o número já traz o país e não deve ganhar
 * o 55 assumido para números locais.
 */
export function telefoneComDdi(ddi: unknown, telefone: unknown): string {
  const numero = String(telefone ?? "").replace(/\D/g, "");
  if (!numero) return "";
  return `${normalizarDdi(ddi)}${numero}`;
}
